import React, { useEffect, useMemo, useState } from 'react';

import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { CardComponent, PageHeader, SettingsForm } from 'components';
import { TextValidatorComponent } from 'components/FormComponents';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { useSubmittingState } from 'hooks';
import { analyticsProviderModel } from 'models/AnalyticsProviderModel';
import { ROUTES } from 'routes/constants';
import { GoogleTagManagerForm } from 'types/other';

import { DisclaimerText, Subtext } from './style';

const defaultFormState: GoogleTagManagerForm = {
  gtmId: '',
};

const GoogleTagManager = () => {
  const { withPageProgressHandler } = useBusyProgressActions();
  const { t } = useTranslation();
  const { handleServerError } = useServerErrorActions();
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();

  const [initialFormState, setInitialFormState] = useState<GoogleTagManagerForm>(defaultFormState);
  const [formState, setFormState] = useState<GoogleTagManagerForm>(defaultFormState);

  useEffect(() => {
    getData();
  }, []);

  const getData = withPageProgressHandler(() =>
    analyticsProviderModel.getGoogleTagManagerSettings()
      .then(analyticsProvider => {
        setInitialFormState(analyticsProvider);
        setFormState(analyticsProvider);
      })
  );

  const handleSubmit = withHandlingSubmittingState(() =>
    analyticsProviderModel
      .updateGoogleTagManagerSettings({ analyticsProvider: formState })
      .then(getData)
      .catch(handleServerError),
  );

  const handleChangeInput = (value: GoogleTagManagerForm) => {
    setFormState(value);
  };

  const showSaveButton = useMemo(() => initialFormState.gtmId !== formState.gtmId, [formState, initialFormState]);

  return (
    <>
      <PageHeader title={t('pages.settings.analyticsProviders.gtm.pageTitle')} backLink={ROUTES.analyticsProviders} />
      <SettingsForm isSaveButtonVisible={showSaveButton} handleSave={handleSubmit} id="gtm">
        <CardComponent title={t('pages.settings.analyticsProviders.gtm.cardTitle')} isLoading={isSubmitting}>
          <Grid container spacing={6} direction="column">
            <Grid item>
              <Subtext>{t('pages.settings.analyticsProviders.gtm.cardSubText')}</Subtext>
            </Grid>
            <Grid item>
              <TextValidatorComponent
                style={{ maxWidth: '468px' }}
                label={t('pages.settings.analyticsProviders.gtm.inputLabel')}
                value={formState.gtmId}
                fieldName="gtmId"
                handleChange={handleChangeInput}
                disableMinHeight
              />
            </Grid>
            <Grid item>
              <DisclaimerText>{t('pages.settings.analyticsProviders.gtm.disclaimer')}</DisclaimerText>
            </Grid>
          </Grid>
        </CardComponent>
      </SettingsForm>
    </>
  );
};

export default GoogleTagManager;
